import React, { useEffect, useState } from "react";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import { ToastTypes, useToast } from "../Components/toast/ToastProvider";
import {
  getSettings,
  saveAppSetting,
} from "../Service/AppSettingsService";

const normalizeGroup = (g) => g.trim().toLowerCase();
const normalizeKey = (k) => k.trim().toLowerCase();

const PRINTER_TYPE_OPTIONS = [
  { value: "thermal", label: "Thermal" },
  { value: "laser", label: "Laser" },
];

function groupSettingsByGroup(list) {
  const map = new Map();
  const order = [];

  for (const s of list) {
    const key = normalizeGroup(s.setting_group);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(s);
  }

  return order.map((key) => {
    const items = map.get(key);
    return { groupLabel: items[0].setting_group, items };
  });
}

const AppSettings = () => {
  const toast = useToast();
  const [settings, setSettings] = useState([]);
  const [settingValues, setSettingValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const settingList = await getSettings();
      setSettings(settingList);

      const initialValues = {};
      settingList.forEach((s) => {
        if (s.id !== undefined) {
          initialValues[s.id] = s.setting_value ?? "";
        }
      });
      setSettingValues(initialValues);
    } catch (err) {
      toast(err.message || "Failed to fetch settings", ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleSetting = (settingId, newValue) => {
    if (settingId === undefined) return;
    setSettingValues((prev) => ({
      ...prev,
      [settingId]: newValue,
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const requests = settings
        .filter((s) => s.id !== undefined)
        .map((s) =>
          saveAppSetting({
            id: s.id,
            setting_group: s.setting_group,
            setting_key: s.setting_key,
            setting_value: settingValues[s.id] ?? "",
          })
        );

      await Promise.all(requests);
      toast("Settings updated successfully", ToastTypes.SUCCESS);
      await fetchData();
    } catch (err) {
      toast(err.message || "Failed to update settings", ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const resetValues = {};

    settings.forEach((s) => {
      if (s.id === undefined) return;
      const nk = normalizeKey(s.setting_key);
      if (nk === "printer type") {
        resetValues[s.id] = "laser";
      } else if (nk === "footer message") {
        resetValues[s.id] = "";
      } else {
        resetValues[s.id] = "false";
      }
    });

    setSettingValues(resetValues);
    toast("All settings reset — click Save All to apply", ToastTypes.SUCCESS);
  };

  const renderControl = (setting) => {
    const id = setting.id;
    if (id === undefined) return null;

    const nk = normalizeKey(setting.setting_key);
    const raw = settingValues[id] ?? "";

    if (nk === "printer type") {
      const known = PRINTER_TYPE_OPTIONS.some((o) => o.value === raw);
      return (
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: "14rem" }}
          id={`setting-printer-${id}`}
          value={raw}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        >
          {!known && raw ? <option value={raw}>{raw}</option> : null}
          {PRINTER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (nk === "footer message") {
      return (
        <textarea
          className="form-control form-control-sm"
          id={`setting-footer-${id}`}
          rows={2}
          value={raw}
          placeholder="Footer text"
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    const isBool = raw === "true" || raw === "false";

    if (!isBool) {
      return (
        <input
          type="text"
          className="form-control form-control-sm"
          id={`setting-text-${id}`}
          value={raw}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    return (
      <div className="d-flex align-items-center gap-3">
        <span
          className={`fw-semibold ${raw === "true" ? "text-success" : "text-danger"}`}
        >
          {raw === "true" ? "SHOW" : "HIDE"}
        </span>
        <div className="form-check form-switch m-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`setting-switch-${id}`}
            checked={raw === "true"}
            onChange={(e) =>
              handleToggleSetting(id, e.target.checked ? "true" : "false")
            }
            style={{ width: "2.5rem", height: "1.3rem" }}
          />
        </div>
      </div>
    );
  };

  const grouped = groupSettingsByGroup(settings);

  return (
    <Main>
      <PageContainer titlePage="Application Settings">
        <div className="container-fluid px-3">
          <div className="text-end my-3">
            <button
              type="button"
              className="btn btn-primary me-3"
              onClick={handleSaveAll}
              disabled={loading || saving || settings.length === 0}
            >
              {saving ? "Saving…" : "Save All"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={loading || settings.length === 0}
            >
              Reset All
            </button>
          </div>

          {loading ? (
            <p className="text-muted">Loading settings…</p>
          ) : settings.length === 0 ? (
            <p className="text-muted">No settings found.</p>
          ) : (
            <div className="row">
              {grouped.map(({ groupLabel, items }) =>
                items.length > 1 ? (
                  <div
                    key={normalizeGroup(groupLabel)}
                    className="col-12 mb-3"
                  >
                    <div className="card shadow-sm border-0 rounded-3">
                      <div className="card-body">
                        <h5 className="fw-semibold mb-3 text-secondary border-bottom pb-2 text-capitalize">
                          {groupLabel}
                        </h5>
                        <div className="d-flex flex-column gap-3">
                          {items.map((setting) => (
                            <div
                              key={setting.id}
                              className="row align-items-center g-2"
                            >
                              <div className="col-12 col-md-4">
                                <label
                                  className="form-label fw-semibold mb-0 text-capitalize"
                                  htmlFor={`setting-control-${setting.id}`}
                                >
                                  {setting.setting_key}
                                </label>
                              </div>
                              <div
                                className="col-12 col-md-8"
                                id={`setting-control-${setting.id}`}
                              >
                                {renderControl(setting)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  items.map((setting) => (
                    <div
                      key={setting.id}
                      className="col-12 mb-3"
                    >
                      <div className="card shadow-sm border-0 rounded-3">
                        <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                          <div>
                            <h6 className="text-secondary text-capitalize mb-1">
                              {setting.setting_group}
                            </h6>
                            <h5 className="fw-semibold mb-0 text-capitalize">
                              {setting.setting_key}
                            </h5>
                          </div>
                          <div
                            className="w-100 align-self-stretch align-self-md-end"
                            style={{ maxWidth: "28rem" }}
                          >
                            {renderControl(setting)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          )}
        </div>
      </PageContainer>
    </Main>
  );
};

export default AppSettings;
